// client/src/components/shared/SearchableSelect.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, X, PlusCircle } from 'lucide-react';

const SearchableSelect = ({ 
    options = [], 
    value, 
    onChange, 
    name, 
    placeholder = 'Selecione...',
    onAddNew,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // ✅ Função para verificar se options tem estrutura de grupos
    const hasOptGroups = useMemo(() => {
        if (!Array.isArray(options) || options.length === 0) return false;
        // Verifica se o primeiro item tem 'label' e 'options'
        return options[0] && options[0].label && Array.isArray(options[0].options);
    }, [options]);

    // ✅ Achatar todos os itens para busca e seleção
    const flatOptions = useMemo(() => {
        if (!hasOptGroups) return options;
        
        const flat = [];
        options.forEach(group => {
            if (Array.isArray(group.options)) {
                flat.push(...group.options);
            }
        });
        return flat;
    }, [options, hasOptGroups]);

    const selectedOption = useMemo(() => {
        if (!value) return null;
        return flatOptions.find(option => String(option.value) === String(value));
    }, [flatOptions, value]);

    // ✅ Filtrar opções (mantendo estrutura de grupos quando não há busca)
    const filteredGroups = useMemo(() => {
        if (!hasOptGroups) return null;
        if (!searchTerm) return options;
        
        const normalizeText = (text = '') => 
            text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        const normalizedSearchTerm = normalizeText(searchTerm);
        
        return options.map(group => ({
            ...group,
            options: group.options.filter(opt =>
                normalizeText(opt.label).includes(normalizedSearchTerm) ||
                (opt.sublabel && normalizeText(opt.sublabel).includes(normalizedSearchTerm))
            )
        })).filter(group => group.options.length > 0);
    }, [options, searchTerm, hasOptGroups]);

    // ✅ Filtrar opções planas (para estrutura sem grupos)
    const filteredFlatOptions = useMemo(() => {
        if (hasOptGroups) return [];
        if (!searchTerm) return options;
        
        const normalizeText = (text = '') => 
            text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        const normalizedSearchTerm = normalizeText(searchTerm);
        
        return options.filter(option =>
            normalizeText(option.label).includes(normalizedSearchTerm) ||
            (option.sublabel && normalizeText(option.sublabel).includes(normalizedSearchTerm))
        );
    }, [options, searchTerm, hasOptGroups]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelectOption = (optionValue) => {
        if (onChange) {
            onChange(optionValue);
        }
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (onChange) {
            onChange('');
        }
    };

    const handleAddNewClick = () => {
        if (onAddNew) {
            setIsOpen(false);
            onAddNew(searchTerm);
        }
    };
    
    const toggleOpen = () => {
        if (!disabled) {
            setIsOpen(prev => !prev);
        }
    };

    // ✅ Verifica se deve mostrar opção de adicionar novo
    const shouldShowAddNew = () => {
        if (!onAddNew) return false;
        if (!searchTerm) return false;
        
        // Verifica se já existe exatamente esse termo
        const exists = flatOptions.some(opt => 
            opt.label.toLowerCase() === searchTerm.toLowerCase()
        );
        return !exists;
    };

    return (
        <div className={`searchable-select ${disabled ? 'is-disabled' : ''}`} ref={wrapperRef}>
            <div className={`select-control ${disabled ? 'select-control-disabled' : ''}`} onClick={toggleOpen}>
                <span className={`select-value ${!selectedOption ? 'is-placeholder' : ''}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <div className="select-indicators">
                    {value && !disabled && (
                        <button type="button" className="indicator-clear" onClick={handleClear} title="Limpar seleção">
                            <X size={16} />
                        </button>
                    )}
                    <span className="indicator-separator"></span>
                    <ChevronDown size={20} className={`indicator-dropdown ${isOpen ? 'is-open' : ''}`} />
                </div>
            </div>
            
            {isOpen && !disabled && (
                <div className="select-menu">
                    <div className="menu-search-input-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            className="menu-search-input"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                    
                    <ul className="menu-options-list">
                        {/* ✅ RENDERIZAÇÃO COM OPTGROUPS */}
                        {hasOptGroups && filteredGroups && filteredGroups.length > 0 ? (
                            filteredGroups.map((group, groupIdx) => (
                                <li key={`group-${groupIdx}`} className="option-group">
                                    <div className="option-group-label">{group.label}</div>
                                    <ul className="option-group-options">
                                        {group.options.map(option => {
                                            const optionKey = option.value !== undefined && option.value !== null 
                                                ? String(option.value) 
                                                : `option-${Math.random()}`;
                                            return (
                                                <li
                                                    key={optionKey}
                                                    className={`option-item ${String(option.value) === String(value) ? 'is-selected' : ''}`}
                                                    onClick={() => handleSelectOption(option.value)}
                                                >
                                                    {option.label}
                                                    {option.sublabel && (
                                                        <span className="option-sublabel">{option.sublabel}</span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            ))
                        ) : !hasOptGroups && filteredFlatOptions.length > 0 ? (
                            // ✅ RENDERIZAÇÃO COM OPÇÕES PLANAS
                            filteredFlatOptions.map(option => {
                                const optionKey = option.value !== undefined && option.value !== null 
                                    ? String(option.value) 
                                    : `option-${Math.random()}`;
                                return (
                                    <li
                                        key={optionKey}
                                        className={`option-item ${String(option.value) === String(value) ? 'is-selected' : ''}`}
                                        onClick={() => handleSelectOption(option.value)}
                                    >
                                        {option.label}
                                        {option.sublabel && (
                                            <span className="option-sublabel">{option.sublabel}</span>
                                        )}
                                    </li>
                                );
                            })
                        ) : null}
                        
                        {/* ✅ BOTÃO PARA ADICIONAR NOVO */}
                        {shouldShowAddNew() && (
                            <li 
                                key="add-new-option"
                                className="option-item is-add-new" 
                                onClick={handleAddNewClick}
                            >
                                <PlusCircle size={18} className="add-new-icon" />
                                <span>Adicionar "<strong>{searchTerm}</strong>" como novo</span>
                            </li>
                        )}

                        {/* ✅ MENSAGEM DE NENHUM RESULTADO */}
                        {!shouldShowAddNew() && (
                            (hasOptGroups && filteredGroups && filteredGroups.length === 0) ||
                            (!hasOptGroups && filteredFlatOptions.length === 0)
                        ) && (
                            <li key="no-results" className="option-item is-disabled">
                                Nenhum resultado para "{searchTerm}"
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;